<template>
  <div class="modal fade show d-block modal-backdrop-custom" tabindex="-1">
    <!-- Success celebration overlay -->
    <Transition name="success-celebration">
      <div v-if="showSuccessAnimation" class="success-overlay">
        <div class="success-content">
          <div class="success-icon-wrapper">
            <font-awesome-icon
              :icon="['fas', 'check-circle']"
              class="success-icon"
            />
          </div>
          <h3 class="success-title">{{ $t("cancellation.success.title") }}</h3>
          <p class="success-message">
            {{ $t("cancellation.success.message") }}
          </p>
        </div>
        <div class="confetti-container">
          <div
            v-for="i in 50"
            :key="i"
            class="confetti"
            :style="getConfettiStyle(i)"
          ></div>
        </div>
      </div>
    </Transition>

    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ $t("cancellation.add_new_statement") }}
            <small v-if="hasAnalyzedData" class="text-muted ms-2">
              <i class="fas fa-robot me-1"></i>
              {{ $t("cancellation.modal.ai_populated") }}
            </small>
          </h5>
          <button type="button" class="btn-close" @click="handleClose"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <!-- File Upload Section -->
            <div class="row mb-4">
              <div class="col-12">
                <h6 class="fw-bold">
                  {{ $t("cancellation.modal.upload_documents") }}
                  <span class="text-danger">*</span>
                </h6>
                <p class="text-muted small">
                  {{ $t("cancellation.modal.upload_description_with_ai") }}
                </p>

                <!-- AI Analysis Status Indicator -->
                <div v-if="isAnalyzing" class="alert alert-info mb-3">
                  <div class="d-flex align-items-center">
                    <div
                      class="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></div>
                    <span>{{
                      $t("cancellation.modal.analyzing_document")
                    }}</span>
                  </div>
                </div>

                <!-- AI Analysis Success Indicator -->
                <div v-if="hasAnalyzedData" class="alert alert-success mb-3">
                  <i class="fas fa-robot me-2"></i>
                  {{ $t("cancellation.modal.ai_analysis_applied") }}
                  <button
                    type="button"
                    class="btn btn-sm btn-secondary ms-2"
                    @click="clearAiData"
                  >
                    {{ $t("cancellation.modal.clear_ai_data") }}
                  </button>
                </div>
                <div
                  class="dropzone"
                  :class="{
                    dragging: isDragging,
                    'border-danger':
                      fileValidation.uploadedFiles.$error ||
                      (showFileErrors && uploadedFiles.length === 0),
                    uploading: isUploading,
                  }"
                  @dragover.prevent="dragOver"
                  @dragleave.prevent="dragLeave"
                  @drop.prevent="handleDrop"
                  @click="triggerFileInput"
                >
                  <input
                    class="form-control visually-hidden"
                    type="file"
                    @change="handleFileUpload"
                    ref="fileInput"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg"
                    multiple
                  />

                  <!-- Upload progress indicator -->
                  <LoadingIndicatorComponent
                    v-if="isUploading"
                    inline
                    class="justify-content-center"
                    :message="$t('cancellation.upload.uploading')"
                  />

                  <!-- Normal upload UI -->
                  <div v-else class="text-center">
                    <font-awesome-icon
                      :icon="['fas', 'cloud-upload-alt']"
                      class="upload-icon fs-1 mb-2"
                    />
                    <p class="mb-1">
                      {{ $t("cancellation.upload.drop_files") }}
                    </p>
                    <small class="text-muted">
                      {{ $t("cancellation.upload.supported_formats") }}
                    </small>
                  </div>
                </div>

                <!-- File upload validation error -->
                <div
                  v-if="
                    fileValidation.uploadedFiles.$error ||
                    (showFileErrors && uploadedFiles.length === 0)
                  "
                  class="text-danger mt-2"
                >
                  <small>{{ t("cancellation.modal.files_required") }}</small>
                </div>

                <!-- Display uploaded files -->
                <div v-if="uploadedFiles.length > 0" class="mt-3">
                  <div class="border rounded p-3">
                    <h6 class="mb-3">
                      {{ $t("cancellation.modal.uploaded_files") }}
                    </h6>
                    <div class="row g-2">
                      <div
                        v-for="(file, index) in uploadedFiles"
                        :key="index"
                        class="col-md-6"
                      >
                        <div
                          class="d-flex align-items-center justify-content-between border rounded p-2"
                        >
                          <div class="d-flex align-items-center">
                            <font-awesome-icon
                              icon="fas fa-file"
                              class="text-muted me-2"
                            />
                            <span
                              class="small text-truncate"
                              :title="file.name"
                              >{{ file.name }}</span
                            >
                          </div>
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-danger"
                            @click="removeFile(index)"
                          >
                            <font-awesome-icon icon="fas fa-trash" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Admin only: CompanyOrder Selection -->
            <div v-if="is_admin" class="row mb-4">
              <div class="col-12">
                <div class="alert alert-subtle-warning admin-extra-info">
                  <h6 class="fw-bold">
                    {{ $t("cancellation.modal.order_selection") }}
                  </h6>
                  <p class="text-muted small">
                    {{ $t("cancellation.modal.order_selection_description") }}
                  </p>

                  <div class="mb-3">
                    <label for="company_order_select" class="form-label">
                      {{ $t("cancellation.modal.linked_order") }}
                    </label>
                    <select
                      id="company_order_select"
                      class="form-select"
                      v-model="selectedOrderId"
                      @change="handleOrderSelection"
                    >
                      <option value="">
                        {{ $t("cancellation.modal.no_order_selected") }}
                      </option>
                      <option
                        v-for="order in availableOrders"
                        :key="order.id"
                        :value="order.id"
                      >
                        {{
                          $t("cancellation.modal.order_option", {
                            id: order.id,
                            date: formatDateTimeIntoDate(order.created_at),
                          })
                        }}
                      </option>
                    </select>
                    <small class="text-muted">
                      {{ $t("cancellation.modal.order_help_text") }}
                    </small>
                  </div>

                  <!-- Statement Selection (shown when order is selected) -->
                  <div
                    v-if="selectedOrderId && availableStatements.length > 0"
                    class="mb-3"
                  >
                    <label for="statement_select" class="form-label">
                      {{ $t("cancellation.modal.statement_selection") }}
                    </label>
                    <select
                      id="statement_select"
                      class="form-select"
                      v-model="selectedStatementIndex"
                      @change="handleStatementSelection"
                    >
                      <option value="">
                        {{ $t("cancellation.modal.no_statement_selected") }}
                      </option>
                      <option
                        v-for="(statement, index) in availableStatements"
                        :key="index"
                        :value="index"
                      >
                        {{ statement.beneficiary_name }} -
                        {{ statement.mwh_cancelled }} MWh
                      </option>
                    </select>
                    <small class="text-muted">
                      {{ $t("cancellation.modal.statement_help_text") }}
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <!-- Cancellation Details Form -->
            <div class="row">
              <div class="col-12">
                <h6 class="fw-bold">
                  {{ $t("cancellation.modal.statement_details") }}
                </h6>

                <!-- Auto-fill notice -->
                <div
                  v-if="selectedOrderId && selectedStatementIndex !== ''"
                  class="alert alert-info mb-3"
                >
                  <font-awesome-icon icon="fas fa-info-circle" class="me-2" />
                  {{ $t("cancellation.modal.auto_fill_from_statement_notice") }}
                </div>
                <div
                  v-else-if="selectedOrderId"
                  class="alert alert-warning mb-3"
                >
                  <font-awesome-icon
                    icon="fas fa-exclamation-triangle"
                    class="me-2"
                  />
                  {{ $t("cancellation.modal.select_statement_notice") }}
                </div>

                <CancelGoStatementComponent
                  :statement="statementData"
                  :statement-index="0"
                  :show-section-titles="true"
                  @update:statement="handleStatementUpdate"
                />
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="handleClose">
            {{ $t("common.cancel") }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="handleSubmit"
            :disabled="isSubmitting || !isFormValid"
          >
            <span
              v-if="isSubmitting"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            {{ $t("cancellation.create_statement") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useValidators } from "@/composables/useValidators";
import { useAiAnalysisHelpers } from "@/dashboard/inventory_management/common/useAiAnalysisHelpers";
import { useDateUtils } from "@/dashboard/inventory_management/common/useDateUtils";
import { FieldUpdater } from "@/dashboard/inventory_management/common/useFieldUpdater";
import CancelGoStatementComponent from "@/dashboard/inventory_management/company_orders/CancelGoStatementComponent.vue";
import type { Company, IndividualCancelStatement } from "@/types";
import { BeneficiaryTypeEnum } from "@/types";
import type { CompanyOrder } from "@/types/cancellation";
import useVuelidate from "@vuelidate/core";
import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { validateCancellationFiles } from "./cancellationFileValidation";

const props = defineProps<{
  company_id?: number | null;
  companies?: Company[];
  is_admin: boolean;
  initialFiles?: File[];
}>();

const emit = defineEmits<{
  close: [];
  "cancellation-created": [];
}>();

const { t } = useI18n();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const validators = useValidators(t);

// Initialize utility composables
const { convertDateTimeToDate, getDefaultPeriodRange } = useDateUtils();
const {
  generateSuccessMessage,
  showMultipleStatementsWarning,
  extractAnalysisData,
} = useAiAnalysisHelpers();

// File upload state
const fileInput = useTemplateRef<HTMLInputElement>("fileInput");
const isDragging = ref(false);
const isUploading = ref(false);
const uploadedFiles = ref<File[]>([]);

// AI Analysis state
const isAnalyzing = ref(false);
const hasAnalyzedData = ref(false);

// Form state
const selectedOrderId = ref<number | "">("");
const selectedStatementIndex = ref<number | "">("");
const availableOrders = ref<CompanyOrder[]>([]);
const availableStatements = ref<any[]>([]);

// Convert old formData structure to new statement structure
const statementData = ref<IndividualCancelStatement>({
  temp_id: "",
  beneficiary_name: "",
  beneficiary_type: BeneficiaryTypeEnum.CONSUMER,
  registry_code: "",
  registry_country_region: "",
  address: "",
  period_start: "",
  period_end: "",
  cancellation_purpose: "",
  mwh_cancelled: null,
  isCollapsed: false,
});

// File validation rules
const fileRules = {
  uploadedFiles: { required: validators.selectionRequired },
};

const fileValidation = useVuelidate(fileRules, { uploadedFiles });

// UI state
const isSubmitting = ref(false);
const showFileErrors = ref(false);
const showSuccessAnimation = ref(false);
const successTimeoutId = ref<ReturnType<typeof setTimeout> | null>(null);

// Confetti style generator
const getConfettiStyle = (index: number) => {
  const colors = [
    "#10b981",
    "#3b82f6",
    "#8b5cf6",
    "#f59e0b",
    "#ef4444",
    "#ec4899",
  ];
  const color = colors[index % colors.length];
  const left = Math.random() * 100;
  const animationDelay = Math.random() * 0.5;
  const animationDuration = 1 + Math.random() * 1;
  const size = 6 + Math.random() * 6;

  return {
    left: `${left}%`,
    backgroundColor: color,
    width: `${size}px`,
    height: `${size}px`,
    animationDelay: `${animationDelay}s`,
    animationDuration: `${animationDuration}s`,
  };
};

// Computed properties
const isFormValid = computed(() => {
  // Check that all required fields have values
  const hasRequiredFields =
    statementData.value.beneficiary_name.trim() !== "" &&
    !!statementData.value.beneficiary_type &&
    statementData.value.registry_code?.trim() !== "" &&
    statementData.value.registry_country_region !== "" &&
    statementData.value.period_start !== "" &&
    statementData.value.period_end !== "" &&
    !!statementData.value.cancellation_purpose &&
    statementData.value.mwh_cancelled !== null &&
    Number(statementData.value.mwh_cancelled) > 0;

  // Check that files are uploaded
  const hasFiles = uploadedFiles.value.length > 0;

  return hasRequiredFields && hasFiles;
});

const targetCompanyId = computed(() => {
  if (props.is_admin && props.companies && props.companies.length === 1) {
    return props.companies[0].id;
  }
  return props.company_id;
});

const targetCompany = computed(() => {
  if (props.companies && props.companies.length === 1) {
    return props.companies[0];
  }
  return null;
});

// File upload methods
const dragOver = () => {
  if (!isUploading.value) {
    isDragging.value = true;
  }
};

const dragLeave = () => {
  if (!isUploading.value) {
    isDragging.value = false;
  }
};

const handleDrop = (event: DragEvent) => {
  if (isUploading.value) return;

  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files && files.length > 0) {
    handleFiles(Array.from(files));
  }
};

const triggerFileInput = () => {
  if (!isUploading.value) {
    fileInput.value?.click();
  }
};

const handleFileUpload = (event: Event) => {
  const input = event.target as HTMLInputElement;
  const files = input.files;
  if (files && files.length > 0) {
    handleFiles(Array.from(files));
  }
};

const handleFiles = async (files: File[]) => {
  const validFiles = validateCancellationFiles(files, (translationKey) =>
    globalErrorHandler(t(translationKey))
  );

  // Add to uploaded files (avoid duplicates)
  validFiles.forEach((file) => {
    const exists = uploadedFiles.value.some(
      (existing) => existing.name === file.name && existing.size === file.size
    );
    if (!exists) {
      uploadedFiles.value.push(file);
    }
  });

  // Trigger AI analysis for each new valid file sequentially
  for (const file of validFiles) {
    await triggerAiAnalysis(file);
  }

  // Reset file input
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

const removeFile = (index: number) => {
  uploadedFiles.value.splice(index, 1);
};

// AI Analysis methods
const triggerAiAnalysis = async (file: File) => {
  if (isAnalyzing.value) return; // Prevent multiple concurrent analyses

  isAnalyzing.value = true;

  try {
    // Create FormData for file upload
    const formData = new FormData();
    formData.append("file", file);

    // Call AI analysis endpoint
    const response = await goSolidApi.post(
      `/api/company/${targetCompanyId.value}/portfolio/analyze-cancel-contract`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    // Process the analysis results
    if (response.data?.data?.form_data) {
      handleFileAnalysisComplete(response.data.data);
    }
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isAnalyzing.value = false;
  }
};

const handleFileAnalysisComplete = async (data: any) => {
  if (!data) {
    hasAnalyzedData.value = false;
    return;
  }

  try {
    const analysisData = extractAnalysisData(data);
    if (!analysisData) return;

    const { hasMultipleStatements, firstStatement, totalStatements } =
      analysisData;
    const isFirstAnalysis = !hasAnalyzedData.value;

    // Use FieldUpdater to process all field updates
    const fieldUpdater = new FieldUpdater(statementData.value);
    fieldUpdater.process(firstStatement, isFirstAnalysis);

    // Global operation notes from AI analysis are order-level and are not stored on
    // the individual cancel statement, so they do not count as a statement-field update.
    const updatedFieldsCount = fieldUpdater.getUpdatedFieldsCount();

    // Mark as AI populated if any fields were updated
    if (updatedFieldsCount > 0) {
      hasAnalyzedData.value = true;
    }

    // Show success message
    const successTitle = t("cancellation.modal.analysis_complete_title");
    const successMessage = generateSuccessMessage(
      hasMultipleStatements,
      updatedFieldsCount,
      totalStatements
    );
    globalSuccessHandler(successTitle, successMessage);

    // Show multiple statements warning if applicable
    if (hasMultipleStatements) {
      showMultipleStatementsWarning(totalStatements, globalErrorHandler);
    }
  } catch (error) {
    globalErrorHandler(error);
  }
};

// AI Data Management
const clearAiData = () => {
  // Reset to default values
  resetStatementData();
  hasAnalyzedData.value = false;
};

// Order selection methods
const handleOrderSelection = async () => {
  // Show confirmation if AI data would be overwritten
  if (hasAnalyzedData.value) {
    const confirmed = confirm(t("cancellation.modal.confirm_override_ai_data"));
    if (!confirmed) {
      // Reset the selection if user cancels
      selectedOrderId.value = "";
      return;
    }
    // Clear AI data indicators when order is selected
    hasAnalyzedData.value = false;
  }

  if (!selectedOrderId.value) {
    resetStatementData();
    availableStatements.value = [];
    selectedStatementIndex.value = "";
    return;
  }

  try {
    // Fetch statements from the selected order
    const response = await goSolidApi.get(
      `/api/company/${targetCompanyId.value}/cancellations/order-statements/${selectedOrderId.value}`
    );

    if (response.data && response.data.data) {
      availableStatements.value = response.data.data.statements || [];

      // global_operation_notes is order-level and is not part of IndividualCancelStatement
    }
  } catch (error) {
    globalErrorHandler(error);
    availableStatements.value = [];
  }

  // Reset statement selection
  selectedStatementIndex.value = "";

  // Reset form data to defaults when order changes
  resetStatementData();
};

const handleStatementSelection = () => {
  if (
    selectedStatementIndex.value === "" ||
    !availableStatements.value.length
  ) {
    resetStatementData();
    return;
  }

  const selectedStatement =
    availableStatements.value[selectedStatementIndex.value];
  if (!selectedStatement) {
    return;
  }

  // Auto-fill from selected statement
  statementData.value = {
    temp_id: "",
    beneficiary_name: selectedStatement.beneficiary_name || "",
    beneficiary_type:
      selectedStatement.beneficiary_type || BeneficiaryTypeEnum.CONSUMER,
    registry_code: selectedStatement.registry_code || "",
    registry_country_region:
      selectedStatement.registry_country_region ||
      statementData.value.registry_country_region ||
      getDefaultStatementData().registry_country_region,
    address: selectedStatement.address || "",
    period_start:
      convertDateTimeToDate(selectedStatement.period_start) ||
      getDefaultStatementData().period_start,
    period_end:
      convertDateTimeToDate(selectedStatement.period_end) ||
      getDefaultStatementData().period_end,
    cancellation_purpose: selectedStatement.cancellation_purpose || "",
    mwh_cancelled: selectedStatement.mwh_cancelled || null,
    isCollapsed: false,
  };
};

const getDefaultStatementData = (): IndividualCancelStatement => {
  // Get company country
  const companyCountry =
    targetCompany.value?.country ||
    (props.companies && props.companies.length === 1
      ? props.companies[0].country
      : "") ||
    "";

  const defaultRange = getDefaultPeriodRange();
  return {
    temp_id: "",
    beneficiary_name: "",
    beneficiary_type: BeneficiaryTypeEnum.CONSUMER,
    registry_code: "",
    registry_country_region: companyCountry,
    address: "",
    period_start: defaultRange.start,
    period_end: defaultRange.end,
    cancellation_purpose: "",
    mwh_cancelled: null,
    isCollapsed: false,
  };
};

const resetStatementData = () => {
  statementData.value = getDefaultStatementData();
};

// Statement update handler
const handleStatementUpdate = (updatedStatement: IndividualCancelStatement) => {
  statementData.value = updatedStatement;
};

// Submit methods
const handleSubmit = async () => {
  showFileErrors.value = true;

  // First check basic validation for immediate feedback
  if (!isFormValid.value) {
    return;
  }

  // Also trigger file validation for additional error display
  await fileValidation.value.$validate();

  isSubmitting.value = true;

  try {
    // Upload files with progress indication
    const uploadedFileIds: number[] = [];

    isUploading.value = true;
    try {
      for (const file of uploadedFiles.value) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        formDataUpload.append("upload_source", "cancellation_statement");

        const uploadResponse = await goSolidApi.post(
          `/api/company/${targetCompanyId.value}/upload`,
          formDataUpload
        );

        if (
          uploadResponse.data &&
          uploadResponse.data.data &&
          uploadResponse.data.data.upload_id
        ) {
          uploadedFileIds.push(uploadResponse.data.data.upload_id);
        }
      }
    } finally {
      isUploading.value = false;
    }

    // Map frontend data to API format - no conversion needed, field names now match
    const cancellationData = {
      beneficiary_name: statementData.value.beneficiary_name,
      beneficiary_type: statementData.value.beneficiary_type,
      registry_code: statementData.value.registry_code,
      registry_country_region: statementData.value.registry_country_region,
      address: statementData.value.address || null,
      period_start: statementData.value.period_start,
      period_end: statementData.value.period_end,
      cancellation_purpose: statementData.value.cancellation_purpose,
      mwh_cancelled: Number(statementData.value.mwh_cancelled),
      company_order_id: selectedOrderId.value || null,
      file_upload_ids: uploadedFileIds,
    };

    // Submit cancellation
    await goSolidApi.post(
      `/api/company/${targetCompanyId.value}/cancellations`,
      cancellationData
    );

    // Show success animation
    showSuccessAnimation.value = true;

    // Wait for animation then emit success (parent handles closing)
    successTimeoutId.value = setTimeout(() => {
      showSuccessAnimation.value = false;
      isSubmitting.value = false;
      emit("cancellation-created");
    }, 1800);
  } catch (error: any) {
    globalErrorHandler(error);
    isSubmitting.value = false;
  }
};

const handleClose = () => {
  // Clear any pending success timeout
  if (successTimeoutId.value) {
    clearTimeout(successTimeoutId.value);
    successTimeoutId.value = null;
  }
  // Reset AI analysis state on modal close
  isAnalyzing.value = false;
  hasAnalyzedData.value = false;
  emit("close");
};

// Cleanup on unmount
onBeforeUnmount(() => {
  if (successTimeoutId.value) {
    clearTimeout(successTimeoutId.value);
    successTimeoutId.value = null;
  }
});

// Lifecycle
onMounted(async () => {
  // Set default date values and company residency
  const companyCountry =
    targetCompany.value?.country ||
    (props.companies && props.companies.length === 1
      ? props.companies[0].country
      : "") ||
    "";

  const defaultRange = getDefaultPeriodRange();
  statementData.value.period_start = defaultRange.start;
  statementData.value.period_end = defaultRange.end;
  statementData.value.registry_country_region = companyCountry;

  try {
    // Fetch available orders for auto-fill
    const response = await goSolidApi.get(
      `/api/company/${targetCompanyId.value}/cancellations/available-orders`
    );

    if (response.data && response.data.data?.orders) {
      availableOrders.value = response.data.data.orders;
    }
  } catch (error: any) {
    globalErrorHandler(error);
  }

  // Process initial files if provided (from drag-and-drop)
  if (props.initialFiles && props.initialFiles.length > 0) {
    handleFiles(props.initialFiles);
  }
});
</script>

<style scoped lang="scss">
.modal {
  z-index: 1055;
}

.modal-backdrop-custom {
  background-color: rgba(0, 0, 0, 0.5);
}

// Success celebration overlay
.success-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.95);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.success-content {
  text-align: center;
  z-index: 10;
}

.success-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  margin-bottom: 1.5rem;
  animation: success-pop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 10px 40px rgba(16, 185, 129, 0.3);
}

.success-icon {
  font-size: 3rem;
  color: white;
}

.success-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--bs-dark);
  margin-bottom: 0.5rem;
  animation: success-fade-up 0.5s ease-out 0.2s both;
}

.success-message {
  font-size: 1rem;
  color: var(--bs-secondary);
  animation: success-fade-up 0.5s ease-out 0.3s both;
}

// Confetti
.confetti-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  overflow: hidden;
}

.confetti {
  position: absolute;
  top: -10px;
  border-radius: 2px;
  animation: confetti-fall linear forwards;
  transform: rotate(0deg);
}

@keyframes success-pop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes success-fade-up {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes confetti-fall {
  0% {
    top: -10px;
    transform: rotate(0deg) translateX(0);
    opacity: 1;
  }
  100% {
    top: 100vh;
    transform: rotate(720deg) translateX(100px);
    opacity: 0;
  }
}

// Vue transition
.success-celebration-enter-active {
  animation: overlay-in 0.3s ease-out;
}

.success-celebration-leave-active {
  animation: overlay-out 0.3s ease-in;
}

@keyframes overlay-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes overlay-out {
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.dropzone {
  border: 3px dashed var(--bs-secondary);
  border-radius: 10px;
  height: 150px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--bs-body-color);
  transition: background-color 0.2s ease-out;

  &.dragging {
    background-color: var(--bs-secondary-bg-subtle);
  }

  &.border-danger {
    border-color: var(--bs-danger) !important;
  }
  &.uploading {
    cursor: not-allowed;
    background-color: var(--bs-light);
    opacity: 0.8;
  }

  &:hover:not(.uploading) {
    background-color: var(--phoenix-primary-bg-subtle);
    color: var(--phoenix-primary);
    transition: background-color 0.2s ease-out;
  }

  .upload-icon {
    color: var(--bs-secondary);
  }
}
</style>
